/**
 * 主布局组件 - 响应式设计
 */

import React, { useState } from 'react';
import { Layout, Menu, Avatar, Dropdown, Space, Typography, Button, Drawer } from 'antd';
import {
  PictureOutlined,
  UserOutlined,
  LogoutOutlined,
  DashboardOutlined,
  MenuOutlined,
  FilterOutlined,
  DollarOutlined,
  MessageOutlined,
  BarChartOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeSwitcher from './ThemeSwitcher';
import type { MenuProps } from 'antd';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [drawerVisible, setDrawerVisible] = useState(false);

  /**
   * 用户下拉菜单
   */
  const userMenuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  /**
   * 侧边栏菜单项
   */
  const sideMenuItems: MenuProps['items'] = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: '数据统计',
      onClick: () => navigate('/dashboard'),
    },
    {
      key: '/users',
      icon: <UserOutlined />,
      label: '用户管理',
      onClick: () => navigate('/users'),
    },
    {
      key: '/purchases',
      icon: <DollarOutlined />,
      label: '充值记录',
      onClick: () => navigate('/purchases'),
    },
    {
      key: '/attribution',
      icon: <BarChartOutlined />,
      label: '增长分析（Prompter）',
      onClick: () => navigate('/attribution'),
    },
    {
      key: '/attribution/vidbom',
      icon: <LineChartOutlined />,
      label: '增长分析（VidBom）',
      onClick: () => navigate('/attribution/vidbom'),
    },
    {
      key: '/filters',
      icon: <FilterOutlined />,
      label: 'Prompt 过滤',
      onClick: () => navigate('/filters'),
    },
    {
      key: '/templates',
      icon: <PictureOutlined />,
      label: '模板管理',
      onClick: () => navigate('/templates'),
    },
    {
      key: '/feedback',
      icon: <MessageOutlined />,
      label: '客诉反馈',
      onClick: () => navigate('/feedback'),
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 桌面端侧边栏 */}
      <Sider
        theme="light"
        width={220}
        breakpoint="lg"
        collapsedWidth="0"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
        }}
        className="desktop-sider"
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--admin-text)',
            fontSize: 18,
            fontWeight: 'bold',
          }}
        >
          Admin Center
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={sideMenuItems}
        />
      </Sider>

      {/* 移动端抽屉菜单 */}
      <Drawer
        title="菜单"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={sideMenuItems.map((item: any) => ({
            ...item,
            onClick: () => {
              item.onClick();
              setDrawerVisible(false);
            },
          }))}
        />
      </Drawer>

      {/* 右侧内容区域 */}
      <Layout style={{ marginLeft: 0 }} className="main-content-layout">
        {/* 顶部导航栏 */}
        <Header
          style={{
            background: 'var(--admin-surface)',
            padding: '0 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          {/* 移动端菜单按钮 */}
          <Button
            type="text"
            icon={<MenuOutlined />}
            aria-label="打开导航菜单"
            onClick={() => setDrawerVisible(true)}
            className="mobile-menu-button"
          />
          
          <Space size={16} style={{ marginLeft: 'auto' }}>
            <ThemeSwitcher />
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <Space style={{ cursor: 'pointer' }}>
                <Avatar
                  size="small"
                  icon={<UserOutlined />}
                  style={{ backgroundColor: 'var(--admin-primary)' }}
                />
                <Text className="username-text">{user?.username}</Text>
                <Text type="secondary" style={{ fontSize: 12 }} className="role-text">
                  ({user?.role})
                </Text>
              </Space>
            </Dropdown>
          </Space>
        </Header>

        {/* 主内容区域 */}
        <Content
          style={{
            margin: '16px',
            padding: '16px',
            background: 'var(--admin-surface)',
            minHeight: 280,
            borderRadius: 8,
          }}
        >
          <Outlet />
        </Content>
      </Layout>

      {/* 响应式样式 */}
      <style>{`
        /* 桌面端：显示侧边栏 */
        @media (min-width: 992px) {
          .desktop-sider {
            display: block !important;
          }
          .main-content-layout {
            margin-left: 220px !important;
          }
          .mobile-menu-button {
            display: none !important;
          }
        }

        /* 移动端：隐藏侧边栏，显示菜单按钮 */
        @media (max-width: 991px) {
          .desktop-sider {
            display: none !important;
          }
          .main-content-layout {
            margin-left: 0 !important;
          }
          .mobile-menu-button {
            display: inline-block !important;
          }
          .username-text,
          .role-text {
            display: none !important;
          }
        }

        /* 平板和小屏幕优化 */
        @media (max-width: 768px) {
          .ant-layout-content {
            margin: 8px !important;
            padding: 12px !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default MainLayout;
